import dayjs from 'dayjs';
import { JOB_STATUSES } from './constants';
import { isShiftChatOpen } from './shiftChat';

/** Shown when bell/push deep link targets a removed, expired, or past shift (T2-6089). */
export const SHIFT_UNAVAILABLE_MESSAGE = 'This shift is no longer available';

const MESSENGER_NOTIFICATION_ROUTES = new Set(['hygienistMessenger', 'messenger']);

const TERMINAL_JOB_STATUSES = new Set<number>([
  JOB_STATUSES.COMPLETED,
  JOB_STATUSES.OFFER_EXPIRED,
  6, // EXPIRED — posting went unfilled
  7, // DELETED — office removed shift
]);

/** Routes that load a temping job by id from notification payload. */
export const NOTIFICATION_ROUTES_REQUIRING_JOB_CHECK = new Set([
  'confirmedOffer',
  'dentistCustomOffers',
  'jobPosted',
  'dentistJobConfirmed',
  'invoice',
  'hygienistInvoice',
  'hygienistMessenger',
  'messenger',
]);

/**
 * T2-6761: stale NEW_OFFER / custom-offer deep links still point at jobPosted
 * (or dentistCustomOffers). Once the shift is booked, send the DO to the
 * confirmed-shift screen instead of Job Details / offers.
 */
const DENTIST_OFFER_NOTIFICATION_ROUTES = new Set([
  'jobPosted',
  'dentistCustomOffers',
]);

export function resolveDentistOfferNotificationRoute(
  route: string | undefined,
  job: { status?: number } | null | undefined,
): string | undefined {
  if (!route) {
    return route;
  }
  if (
    DENTIST_OFFER_NOTIFICATION_ROUTES.has(route)
    && Number(job?.status) === JOB_STATUSES.CONFIRMED
  ) {
    return 'dentistJobConfirmed';
  }
  return route;
}

export function extractNotificationJobId(
  route?: string,
  params?: Record<string, unknown>,
  notificationJobId?: number | null,
): number | null {
  if (!route || !NOTIFICATION_ROUTES_REQUIRING_JOB_CHECK.has(route)) {
    return null;
  }
  const fromParams = params?.jobId != null ? Number(params.jobId) : NaN;
  const resolved = Number.isFinite(fromParams) && fromParams > 0
    ? fromParams
    : (notificationJobId != null ? Number(notificationJobId) : NaN);
  return Number.isFinite(resolved) && resolved > 0 ? resolved : null;
}

export function isJobUnavailableForNotificationNav(
  job: { status?: number; startDate?: string | Date | null; shifts?: unknown[] } | null | undefined,
  route?: string,
): boolean {
  if (!job || job.status == null) {
    return true;
  }

  // T2-7143: invoices are created for completed shifts, so the generic stale
  // shift rules below are not valid for this route. The job lookup still has
  // to succeed, preserving the ownership/existence check before navigation.
  if (route === 'invoice' || route === 'hygienistInvoice') {
    return false;
  }

  // T2-6345: completed shifts stay reachable via messenger for 48h after shift end.
  if (route && MESSENGER_NOTIFICATION_ROUTES.has(route)) {
    return !isShiftChatOpen(job);
  }

  if (TERMINAL_JOB_STATUSES.has(Number(job.status))) {
    return true;
  }
  if (job.startDate) {
    const shiftDay = dayjs(String(job.startDate).substring(0, 10)).startOf('day');
    if (shiftDay.isBefore(dayjs().startOf('day'))) {
      return true;
    }
  }
  return false;
}
